import {
  CategoricalDomain,
  CategoricalFilter,
  DataValue,
  Filter,
  FilterType,
  HierarchicalDataValue,
  HierarchicalFilter,
  HierarchicalLevelInfo,
  RangeDomain,
  RangeFilter,
  RelativeDateFilter,
} from '@tableau/api-internal-contract-js';
import {
  DataType,
  FiltersQuantitativeIncludedValues as IncludedValues,
  FilterType as PresLayerFilterType,
} from '../../gen-src/preslayer/Enums';
import {
  CategoricalDomainPresModel,
  FilterInfoPresModel,
  FilterValuePresModel,
  HierarchicalFilterValuePresModel,
  HierarchicalLevelInfoPresModel,
  QuantitativeDomainPresModel,
} from '../../gen-src/preslayer/PresModels';
import { PresLayerToApiEnumMappings as EnumMappings } from './EnumMappings';
import { PresLayerToSharedApiConverter } from './PresLayerToSharedApiConverter';

export class FiltersConverter {
  public static convertFiltersPresModel(filterPms: Array<FilterInfoPresModel>): Array<Filter> {
    const filters: Array<Filter> = [];
    filterPms.forEach((filterPm) => {
      switch (filterPm.filterType) {
        case PresLayerFilterType.FLTR_Categorical: {
          filters.push(FiltersConverter.convertCategoricalFilterPresModel(filterPm));
          break;
        }

        case PresLayerFilterType.FLTR_Quantitative: {
          filters.push(FiltersConverter.convertQuantitativeFilterPresModel(filterPm));
          break;
        }

        case PresLayerFilterType.FLTR_RelativeDate: {
          filters.push(FiltersConverter.convertRelativeDateFilterPresModel(filterPm));
          break;
        }

        case PresLayerFilterType.FLTR_Hierarchical: {
          filters.push(FiltersConverter.convertHierarchicalFilterPresModel(filterPm));
          break;
        }

        default:
          return;
      }
    });
    return filters;
  }

  public static convertCategoricalFilterPresModel(filterPm: FilterInfoPresModel): CategoricalFilter {
    const selectedValues: Array<DataValue> = filterPm.categoricalFilterInfo.captionedFilterValueList.map((filterValue) => {
      return FiltersConverter.convertFilterValuePresModel(filterValue);
    });
    const filter: CategoricalFilter = {
      values: selectedValues,
      isExclude: filterPm.categoricalFilterInfo.exclude,
      isAllSelected: filterPm.categoricalFilterInfo.allFilterOptionSelected,
      filterType: FilterType.Categorical,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
    };
    return filter;
  }

  public static convertHierarchicalFilterPresModel(filterPm: FilterInfoPresModel): HierarchicalFilter {
    const selectedValues: Array<HierarchicalDataValue> = filterPm.hierarchicalFilterInfo.hierarchicalFilterValueList.map((filterValue) => {
      return FiltersConverter.convertHierarchicalFilterValuePresModel(filterValue);
    });
    const levelInfo: Array<HierarchicalLevelInfo> = filterPm.hierarchicalFilterInfo.levelInfoList.map((levelInfo) => {
      return FiltersConverter.convertHierarchicalLevelInfoPresModel(levelInfo);
    });
    const filter: HierarchicalFilter = {
      dimensionName: filterPm.hierarchicalFilterInfo.hiearchicalDimensionName,
      hierarchyCaption: filterPm.hierarchicalFilterInfo.hierarchyCaption,
      values: selectedValues,
      levels: filterPm.hierarchicalFilterInfo.levelCount,
      levelInfo: levelInfo,
      isAllSelected: filterPm.hierarchicalFilterInfo.allFilterOptionSelected,
      filterType: FilterType.Hierarchical,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
    };
    return filter;
  }

  public static convertQuantitativeFilterPresModel(filterPm: FilterInfoPresModel): RangeFilter {
    const minValue: DataValue = FiltersConverter.convertFilterValuePresModel(filterPm.quantitativeFilterInfo.captionedRangeMin);
    const maxValue: DataValue = FiltersConverter.convertFilterValuePresModel(filterPm.quantitativeFilterInfo.captionedRangeMax);
    const filter: RangeFilter = {
      filterType: FilterType.Range,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
      min: minValue,
      max: maxValue,
      includeNullValues: FiltersConverter.convertIncludedNullValues(filterPm.quantitativeFilterInfo.included),
    };
    return filter;
  }

  public static convertRelativeDateFilterPresModel(filterPm: FilterInfoPresModel): RelativeDateFilter {
    const anchorDateValue: DataValue = FiltersConverter.convertFilterValuePresModel(filterPm.relativeDateFilterInfo.captionedAnchorDate);
    const filter: RelativeDateFilter = {
      filterType: FilterType.RelativeDate,
      fieldCaption: filterPm.fieldCaption,
      fieldName: filterPm.fn,
      visualId: PresLayerToSharedApiConverter.convertVisualId(filterPm.visualIdPresModel),
      anchorDate: anchorDateValue,
      periodType: EnumMappings.dateStepPeriod.convert(filterPm.relativeDateFilterInfo.datePeriodType),
      rangeType: EnumMappings.dateRangeType.convert(filterPm.relativeDateFilterInfo.dateRangeType),
      rangeN: filterPm.relativeDateFilterInfo.rangeN,
    };
    return filter;
  }

  public static convertCategoricalDomain(domainPm: CategoricalDomainPresModel): CategoricalDomain {
    const domainValues: Array<DataValue> = domainPm.captionedFilterValueList.map((filterValue) => {
      return FiltersConverter.convertFilterValuePresModel(filterValue);
    });
    return { values: domainValues };
  }

  public static convertQuantitativeDomain(domainPm: QuantitativeDomainPresModel): RangeDomain {
    const minValue: DataValue = FiltersConverter.convertFilterValuePresModel(domainPm.captionedRangeMin);
    const maxValue: DataValue = FiltersConverter.convertFilterValuePresModel(domainPm.captionedRangeMax);
    return {
      max: maxValue,
      min: minValue,
    };
  }

  public static convertFilterValuePresModel(dvPM: FilterValuePresModel): DataValue {
    /*tslint:disable-next-line:no-any */
    let value: any;
    switch (dvPM.dataType) {
      case DataType.Boolean: {
        value = Boolean(dvPM.dataValueStr);
        break;
      }
      case DataType.Date: {
        value = new Date(dvPM.dataValueStr);
        value = Number.isNaN(value.getTime()) ? undefined : value;
        break;
      }
      case DataType.DateTime: {
        value = new Date(dvPM.dataValueStr);
        value = Number.isNaN(value.getTime()) ? undefined : value;
        break;
      }
      case DataType.Integer: {
        value = Number.parseInt(dvPM.dataValueStr, 10);
        break;
      }
      case DataType.Real: {
        value = Number.parseFloat(dvPM.dataValueStr);
        break;
      }
      // default to string
      default: {
        value = dvPM.dataValueStr;
        break;
      }
    }
    return {
      value: value,
      // TODO: no easy way of getting back aliased value from platform, will need to investigate further
      aliasedValue: '',
      formattedValue: dvPM.dataValueDisplayString,
    };
  }

  public static convertHierarchicalFilterValuePresModel(filterValue: HierarchicalFilterValuePresModel): HierarchicalDataValue {
    return {
      value: FiltersConverter.convertFilterValuePresModel(filterValue.captionedFilterValue),
      hierarchicalPath: filterValue.hierarchicalPath,
      level: filterValue.levelNumber,
    };
  }

  public static convertHierarchicalLevelInfoPresModel(aLevel: HierarchicalLevelInfoPresModel): HierarchicalLevelInfo {
    return {
      name: aLevel.levelName,
      levelSelectionState: EnumMappings.hierarchicalLevelSelectionStateType.convert(aLevel.levelSelectionState),
    };
  }

  public static convertIncludedNullValues(includeNull: IncludedValues): boolean {
    return includeNull === IncludedValues.InRangeOrNull || includeNull === IncludedValues.All || includeNull === IncludedValues.Null;
  }
}
